import { CardBody, CardFooter, Icon, Separator, SubmitButton, TextControl } from "@dgs/core";
import { Form, useField } from "formik";
import { TFunction } from "i18next";
import { FC } from "react";
import { useTranslation } from "react-i18next";
import styled from "styled-components";
import { ProfileSettingsResource, VCardFieldName, VCardFieldResource } from "../shared/types/profileSettings";
import { ContactAttributeSingleSelectField } from "./ContactAttributeSingleSelectField";
import { ProfileContactViewSelect } from "./ProfileContactViewSelect";

const Heading = styled.span`
	display: block;
	padding-top: ${({ theme }) => theme.spacing(4)};
	padding-bottom: ${({ theme }) => theme.spacing(5)};
	font-weight: ${({ theme }) => theme.fonts.weights.bold};
`;

const Info = styled.div`
	display: flex;
	align-items: center;
	gap: ${({ theme }) => theme.spacing(2)};
	padding-bottom: ${({ theme }) => theme.spacing(5)};
`;

const InfoIcon = styled(Icon)`
	height: 1.5rem;
	width: 1.5rem;
`;

export const getLabel = (vCardFieldName: VCardFieldName, t: TFunction) => {
	switch (vCardFieldName) {
		case VCardFieldName.ADR:
			return t("Address");
		case VCardFieldName.BDAY:
			return t("Date of Birth");
		case VCardFieldName.EMAIL:
			return t("Email");
		case VCardFieldName.GEO:
			return t("Location");
		case VCardFieldName.KEY:
			return t("Public key");
		case VCardFieldName.LABEL:
			return t("Label");
		case VCardFieldName.LANG:
			return t("Language");
		case VCardFieldName.NOTE:
			return t("Supplemental information");
		case VCardFieldName.ORG:
			return t("Company");
		case VCardFieldName.REV:
			return t("Revision");
		case VCardFieldName.ROLE:
			return t("Role");
		case VCardFieldName.TEL:
			return t("Phone number");
		case VCardFieldName.TITLE:
			return t("Job title");
		case VCardFieldName.TZ:
			return t("Time zone");
		case VCardFieldName.UID:
			return t("Identifier");
		case VCardFieldName.URL:
			return t("URL");
	}
};

interface Props {
	initialValues: ProfileSettingsResource;
}

export const ProfileSettingsForm: FC<Props> = ({ initialValues }) => {
	const { t } = useTranslation();
	const [{ value }, , { setValue }] = useField<VCardFieldResource[]>("vCardFields");

	return (
		<Form>
			<CardBody>
				<Info>
					<InfoIcon $icon="info" />
					{t(
						"A contact view must include at least the first name, last name, primary email and primary company to be eligible for selection as a profile contact view.",
					)}
				</Info>
				<ProfileContactViewSelect
					name="defaultContactView"
					label={t("Profile contact view")}
					onChange={() =>
						setValue(
							value.map((v) => ({
								...v,
								contactAttribute: null,
							})),
						)
					}
				/>
				<Separator />
				<Heading>{t("VCard fields")}</Heading>
				<TextControl
					name="name"
					label={t("Name")}
					value={`${t("Contact last name")}, ${t("Contact first name")}`}
					disabled
					required
				/>
				<TextControl
					name="formattedName"
					label={t("Formatted name")}
					value={`${t("Contact first name")}, ${t("Contact last name")}`}
					disabled
					required
				/>
				{initialValues.vCardFields.map((vCardField, i) => (
					<ContactAttributeSingleSelectField
						key={vCardField.name}
						name={`vCardFields.${i}.contactAttribute`}
						label={getLabel(vCardField.name, t)}
						infoText={vCardField.name}
						required={vCardField.name === VCardFieldName.EMAIL}
					/>
				))}
			</CardBody>
			<CardFooter>
				<SubmitButton>{t("Save")}</SubmitButton>
			</CardFooter>
		</Form>
	);
};
